import { useEffect, useState } from "react";
import { Bell, BellOff, Loader2, Send } from "lucide-react";
import {
  useVapidPublicKey,
  usePushRules,
  useSubscribePush,
  useUnsubscribePush,
  useUpdatePushRules,
  useTestPush,
  useGroups,
  useEAs,
} from "../hooks/useDashboardData";
import { isPushSupported, getExistingSubscription, subscribeToPush } from "../lib/push";

const isIos = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
const isStandalone = window.matchMedia("(display-mode: standalone)").matches;

export function NotificationSettings() {
  const [endpoint, setEndpoint] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const vapidQuery = useVapidPublicKey();
  const rulesQuery = usePushRules(endpoint ?? undefined);
  const subscribeMutation = useSubscribePush();
  const unsubscribeMutation = useUnsubscribePush();
  const updateRulesMutation = useUpdatePushRules();
  const testMutation = useTestPush();
  const { data: groupsData } = useGroups();
  const { data: easData } = useEAs();

  useEffect(() => {
    getExistingSubscription()
      .then((sub) => setEndpoint(sub?.endpoint ?? null))
      .finally(() => setChecking(false));
  }, []);

  async function handleEnable() {
    setError(null);
    try {
      if (!vapidQuery.data) throw new Error("Could not load the notification key. Try again.");
      const sub = await subscribeToPush(vapidQuery.data.publicKey);
      const json = sub.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
        throw new Error("Browser returned an incomplete subscription");
      }
      await subscribeMutation.mutateAsync({
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      });
      setEndpoint(json.endpoint);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to enable notifications");
    }
  }

  async function handleDisable() {
    setError(null);
    try {
      const sub = await getExistingSubscription();
      if (endpoint) await unsubscribeMutation.mutateAsync(endpoint);
      if (sub) await sub.unsubscribe();
      setEndpoint(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to disable notifications");
    }
  }

  if (checking) {
    return (
      <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5 text-sm text-slate-500">
        Checking notification status...
      </div>
    );
  }

  if (!isPushSupported()) {
    return (
      <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5">
        <div className="mb-2 flex items-center gap-2">
          <BellOff className="h-4 w-4 text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-100">Notifications</h3>
        </div>
        <p className="text-sm text-slate-500">Push notifications aren't supported in this browser.</p>
      </div>
    );
  }

  if (isIos && !isStandalone) {
    return (
      <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5">
        <div className="mb-2 flex items-center gap-2">
          <Bell className="h-4 w-4 text-emerald-400" />
          <h3 className="text-sm font-semibold text-slate-100">Notifications</h3>
        </div>
        <p className="text-sm text-slate-500">
          On iPhone/iPad, add this dashboard to your Home Screen first (Share &rarr; Add to Home Screen),
          then open it from there to enable notifications - Safari can't deliver push notifications to a
          regular browser tab.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-emerald-400" />
          <h3 className="text-sm font-semibold text-slate-100">Notifications</h3>
        </div>
        {endpoint ? (
          <button
            onClick={handleDisable}
            disabled={unsubscribeMutation.isPending}
            className="flex items-center gap-1.5 rounded-lg border border-rose-800/60 px-3 py-1.5 text-sm font-medium text-rose-400 transition-colors hover:bg-rose-950/40 disabled:opacity-60"
          >
            <BellOff className="h-3.5 w-3.5" />
            Disable on this device
          </button>
        ) : (
          <button
            onClick={handleEnable}
            disabled={subscribeMutation.isPending || !vapidQuery.data}
            className="flex items-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-1.5 text-sm font-semibold text-slate-950 transition-colors hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {subscribeMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Enable on this device
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-rose-900 bg-rose-950/50 px-3 py-2 text-sm text-rose-400">
          {error}
        </div>
      )}

      {endpoint && rulesQuery.data && (
        <RuleForm
          endpoint={endpoint}
          rules={rulesQuery.data.rules}
          groups={groupsData?.groups ?? []}
          eas={easData?.eas ?? []}
          onSave={(rules) => updateRulesMutation.mutate(rules)}
          saving={updateRulesMutation.isPending}
          onTest={() => testMutation.mutate(endpoint)}
          testing={testMutation.isPending}
          testResult={testMutation.isSuccess ? "sent" : testMutation.isError ? "error" : null}
        />
      )}

      {endpoint && !rulesQuery.data && rulesQuery.isLoading && (
        <p className="text-sm text-slate-500">Loading alert settings...</p>
      )}
    </div>
  );
}

function RuleForm({
  endpoint,
  rules,
  groups,
  eas,
  onSave,
  saving,
  onTest,
  testing,
  testResult,
}: {
  endpoint: string;
  rules: {
    enabled: number;
    floating_loss_threshold: number | null;
    margin_level_threshold: number | null;
    status_alerts: number;
    watch_all: number;
    group_ids: number[];
    ea_ids: number[];
  };
  groups: { id: number; name: string }[];
  eas: { id: number; account_name: string | null; ea_name: string }[];
  onSave: (rules: {
    endpoint: string;
    enabled: boolean;
    floating_loss_threshold: number | null;
    margin_level_threshold: number | null;
    status_alerts: boolean;
    watch_all: boolean;
    group_ids: number[];
    ea_ids: number[];
  }) => void;
  saving: boolean;
  onTest: () => void;
  testing: boolean;
  testResult: "sent" | "error" | null;
}) {
  const [enabled, setEnabled] = useState(Boolean(rules.enabled));
  const [floatingLoss, setFloatingLoss] = useState(rules.floating_loss_threshold?.toString() ?? "");
  const [marginLevel, setMarginLevel] = useState(rules.margin_level_threshold?.toString() ?? "");
  const [statusAlerts, setStatusAlerts] = useState(Boolean(rules.status_alerts));
  const [watchAll, setWatchAll] = useState(Boolean(rules.watch_all));
  const [groupIds, setGroupIds] = useState<Set<number>>(new Set(rules.group_ids));
  const [eaIds, setEaIds] = useState<Set<number>>(new Set(rules.ea_ids));

  useEffect(() => {
    setEnabled(Boolean(rules.enabled));
    setFloatingLoss(rules.floating_loss_threshold?.toString() ?? "");
    setMarginLevel(rules.margin_level_threshold?.toString() ?? "");
    setStatusAlerts(Boolean(rules.status_alerts));
    setWatchAll(Boolean(rules.watch_all));
    setGroupIds(new Set(rules.group_ids));
    setEaIds(new Set(rules.ea_ids));
  }, [rules]);

  function toggleSetItem(set: Set<number>, id: number, setSet: (s: Set<number>) => void) {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSet(next);
  }

  function handleSave() {
    onSave({
      endpoint,
      enabled,
      floating_loss_threshold: floatingLoss === "" ? null : Number(floatingLoss),
      margin_level_threshold: marginLevel === "" ? null : Number(marginLevel),
      status_alerts: statusAlerts,
      watch_all: watchAll,
      group_ids: [...groupIds],
      ea_ids: [...eaIds],
    });
  }

  return (
    <div className="space-y-4 border-t border-slate-800/70 pt-4">
      <label className="flex items-center gap-2 text-sm text-slate-300">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="h-4 w-4 rounded border-slate-600 bg-slate-900 accent-emerald-500"
        />
        Notifications enabled on this device
      </label>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-300">
            Floating loss alert ($)
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={floatingLoss}
            onChange={(e) => setFloatingLoss(e.target.value)}
            placeholder="Off"
            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none ring-emerald-500/50 placeholder:text-slate-600 focus:ring-2"
          />
          <p className="mt-1 text-xs text-slate-500">Alerts when floating drops to/below -${floatingLoss || "…"}.</p>
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-300">Margin level alert (%)</label>
          <input
            type="number"
            min="0"
            step="1"
            value={marginLevel}
            onChange={(e) => setMarginLevel(e.target.value)}
            placeholder="Off"
            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none ring-emerald-500/50 placeholder:text-slate-600 focus:ring-2"
          />
          <p className="mt-1 text-xs text-slate-500">Alerts when margin level falls to/below {marginLevel || "…"}%.</p>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-300">
        <input
          type="checkbox"
          checked={statusAlerts}
          onChange={(e) => setStatusAlerts(e.target.checked)}
          className="h-4 w-4 rounded border-slate-600 bg-slate-900 accent-emerald-500"
        />
        Notify when an EA goes offline or comes back online
      </label>

      <label className="flex items-center gap-2 text-sm text-slate-300">
        <input
          type="checkbox"
          checked={watchAll}
          onChange={(e) => setWatchAll(e.target.checked)}
          className="h-4 w-4 rounded border-slate-600 bg-slate-900 accent-emerald-500"
        />
        Watch all EAs
      </label>

      {!watchAll && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <p className="mb-1.5 text-sm font-medium text-slate-300">Groups</p>
            <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-slate-800 p-2">
              {groups.length === 0 ? (
                <p className="text-xs text-slate-600">No groups.</p>
              ) : (
                groups.map((g) => (
                  <label key={g.id} className="flex items-center gap-2 text-sm text-slate-300">
                    <input
                      type="checkbox"
                      checked={groupIds.has(g.id)}
                      onChange={() => toggleSetItem(groupIds, g.id, setGroupIds)}
                      className="h-3.5 w-3.5 rounded border-slate-600 bg-slate-900 accent-emerald-500"
                    />
                    {g.name}
                  </label>
                ))
              )}
            </div>
          </div>
          <div>
            <p className="mb-1.5 text-sm font-medium text-slate-300">Individual EAs</p>
            <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-slate-800 p-2">
              {eas.length === 0 ? (
                <p className="text-xs text-slate-600">No EAs.</p>
              ) : (
                eas.map((ea) => (
                  <label key={ea.id} className="flex items-center gap-2 text-sm text-slate-300">
                    <input
                      type="checkbox"
                      checked={eaIds.has(ea.id)}
                      onChange={() => toggleSetItem(eaIds, ea.id, setEaIds)}
                      className="h-3.5 w-3.5 rounded border-slate-600 bg-slate-900 accent-emerald-500"
                    />
                    {ea.account_name || ea.ea_name}
                  </label>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 pt-1">
        <button
          onClick={handleSave}
          disabled={saving}
          className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-950 transition-colors hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? "Saving..." : "Save"}
        </button>
        <button
          onClick={onTest}
          disabled={testing}
          className="flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:border-slate-600 hover:bg-slate-800 disabled:opacity-60"
        >
          <Send className="h-3.5 w-3.5" />
          Send test notification
        </button>
        {testResult === "sent" && <span className="text-sm text-emerald-400">Sent.</span>}
        {testResult === "error" && <span className="text-sm text-rose-400">Failed to send.</span>}
      </div>
    </div>
  );
}
